/**
 * Landing page builder — verbatim port of CreateEventLanding + LandingPageEditor.
 * Two modes: embedded (the hubs surface) and standalone editor (landing.editor).
 * The create.landing wizard step was removed 2026-08-24 with the event wizard.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Btn, Dropdown, Icon, IconBtn, Input, T, tx } from "@v0y4ge/jrni-ui";
import { useNav } from "../../shell/router";
import { Screen, PageHeader } from "../../shell/chrome";
import { eventsSectionTabs } from "../../shell/list-composites";
import { useEventSectionCounts } from "../events/eventSectionCounts";
import { upsertLandingPage } from "./LandingPagesIndex";
import { createLandingPage, updateLandingPage, publishLandingPage, getLandingPage, getStudioEvent } from "@v0y4ge/api-client";
import { studioRequestOptions } from "../../lib/studio-config";
import { toast } from "../../shell/toast";
import { CreationStore, useCreationConfig } from "../events/creation-store";
import { useBranding } from "../settings/useBranding";
import { useStaff } from "../staff/useStaff";
import { publicHost, withUtm, SHARE_SOURCES, SHARE_MEDIUM } from "../../lib/public-url";
import { friendlyDate, friendlyTimeRange, resolveEventDayHours } from "../../lib/datetime";
import {
  LANDING_BLOCKS, LANDING_THEMES, LandingBlock, LandingTheme,
  landingBlocksFor,
  LandingPreview, BlockInspector,
  stdFieldsToRegistrationQuestions,
  findBlockDeep, parentRowOf, updateCfgDeep, removeBlockDeep, moveBlockDeep,
  insertBeforeDeep, appendToCellDeep, getRowCells, CELL_FORBIDDEN,
  cloneBlockDeep, insertAfterDeep,
  type PublicEventDetailLike, bareZone } from "./landing-builder-helpers";
import { useT, t as i18nT } from "../../lib/i18n";

import { t } from "@v0y4ge/i18n-runtime";
/** Blocks/theme/font (+ canonical event) of an EXISTING saved page — passed by
 *  the standalone editor route after it fetches via getLandingPage. */
interface InitialLandingPage { id?: string; blocks?: LandingBlock[]; theme?: string; font?: string; event?: PublicEventDetailLike | null }

/** A single per-event landing page targeting a subset of the event's ticket
 *  types (event-level model: one event → many pages, each scoped to ticket
 *  types). Stored in the creation config as `cfg.landingPages`. */
export interface EventLandingPage {
  id: string;
  name: string;
  slug: string;
  ticketTypeIds: string[];
  ticketGroupIds: string[];
  design: { blocks: LandingBlock[]; theme: string; font: string };
  status: "draft" | "published";
}

/** What the embedded builder writes back on save — design + meta only. The
 *  manager owns ticket-type / ticket-group targeting and merges this in. */
export interface EmbeddedLandingSave {
  id: string;
  name: string;
  slug: string;
  design: { blocks: LandingBlock[]; theme: string; font: string };
  status: "draft" | "published";
}

// ── Theme helpers ───────────────────────────────────────────────────────
/** The "Brand" theme reflects the workspace branding (primary + cover + body
 * font) so a new landing page matches the current brand out of the box. */
function getDefaultTheme(branding?: { primary?: string; cover?: string; bodyFont?: string }): LandingTheme {
  const brand = branding?.primary || T.p1;
  return { id: "brand", name: i18nT("landing.builder.brand", "Brand"), brand, hero: [branding?.primary || "#75ADFA", branding?.cover || T.p1], font: branding?.bodyFont || "DM Sans" };
}

/** Derive landing-page content from the event details entered in the wizard,
 * so a new page auto-reflects the date/time, location, address and hero image.
 * Exported: the in-app event preview (PublicEventLanding, eventId path) shares
 * this + defaultLandingBlocks + previewEventFromCfg so the eyeball preview is
 * LITERALLY the builder step's render logic (operator mandate, 2026-07-14). */
export function deriveEventDetails(cfg: Record<string, unknown>) {
  const startStr = String(cfg.start || "");
  const endStr = String(cfg.end || "");
  const datePart = startStr ? friendlyDate(startStr) : "";
  const timeStr = startStr ? friendlyTimeRange(startStr, endStr || undefined) : "";
  const locStr = String(cfg.location || (cfg as { venueName?: string }).venueName || "");
  const address = String((cfg as { address?: string }).address || cfg.location || "");
  const heroImg = cfg.heroImage ? String(cfg.heroImage) : "";
  const pill = String((cfg as { label?: string }).label || (cfg as { category?: string }).category || "Event");
  const descRaw = String(cfg.description || "").replace(/[#*_>`]/g, "").replace(/\s+/g, " ").trim();
  const subhead = descRaw ? (descRaw.length > 180 ? descRaw.slice(0, 177) + "…" : descRaw) : "";
  return { datePart, timeStr, locStr, address, heroImg, pill, subhead };
}

/** The default block template for an event with no saved landing page — the
 * SAME template the builder starts from, shared with the in-app event preview
 * so both render identically for an undesigned event. */
export function defaultLandingBlocks(cfg: Record<string, unknown>, title: string): LandingBlock[] {
  const d = deriveEventDetails(cfg);
  // ui-config — default block template for a new landing page (not tenant data)
  return [
    { uid: "b1", type: "hero",    cfg: { headline: title, subhead: d.subhead || "Join us for a curated program of talks, demos, and networking.", ...(d.heroImg ? { bgKind: "image", imageUrl: d.heroImg } : { bgKind: "gradient" }), from: "#75ADFA", to: "#5142C7", solid: "#5142C7", padding: 40, align: "left", showCTA: true, ctaLabel: "Register · Free", pill: d.pill } },
    { uid: "b2", type: "dates",   cfg: { date: d.datePart || "Date TBD", time: d.timeStr, loc: d.locStr, style: "card" } },
    // Req 5 (Option-2 docket) — the registration capture form is a DEFAULT
    // block: present on every new page without the operator adding it, directly
    // under Hero + Date (operator, 2026-08-01). (The matching default-compose
    // for already-published surfaces is the declared data-layer seed ask.)
    { uid: "b4", type: "signup",  cfg: { title: t("landing.builder.title", "Register"), cta: "Register", showName: true, showCo: false } },
    { uid: "b3", type: "tickets", cfg: { title: t("landing.builder.title2", "Tickets"), layout: "list", showSold: false } },
  ];
}

/** PublicEventDetailLike derived from a wizard-cfg-shaped record — the builder
 * preview's live hydration (operator item 2, 2026-07-09), shared verbatim with
 * the in-app event preview. */
export function previewEventFromCfg(cfgAny: Record<string, any>): PublicEventDetailLike {
  const t = (cfgAny.tickets || []) as Array<{ id?: string; name?: string; desc?: string; price?: number; kind?: string; sold?: number }>;
  const ticketTiers = t.map((tk, i) => ({
    id: String(tk.id ?? `t${i}`),
    name: String(tk.name ?? "Ticket"),
    desc: tk.desc ? String(tk.desc) : undefined,
    price: (tk.kind === "free" || tk.kind === "code" || !tk.price) ? 0 : Number(tk.price),
    // Carries the wizard row's kind so tierRequiresPayment() detects paid tiers
    // in the builder preview exactly like the published surface (Req 4).
    kind: tk.kind ? String(tk.kind) : undefined,
    sold: typeof tk.sold === "number" ? tk.sold : undefined,
  }));
  const address = typeof cfgAny.address === "string" ? cfgAny.address : "";
  return {
    title: String(cfgAny.title || "") || undefined,
    /* The countdown reads a bare wall-clock target in the event's own zone (CC 0251). */
    timezone: bareZone(cfgAny.timezone) || undefined,
    ticketTiers,
    // Req 5 — WYSIWYG = what publish emits. The event's own registrationQuestions
    // render verbatim when non-empty; otherwise mirror CreateEventPublish's #816
    // mapping INCLUDING its defaults-when-step-never-opened behavior ("Defaults
    // mirror the Registration step's initial state") — so the landing form always
    // matches both the Registration step and the published payload.
    registrationQuestions: (() => {
      const own = cfgAny.registrationQuestions;
      if (Array.isArray(own) && own.length) return own as PublicEventDetailLike["registrationQuestions"];
      return stdFieldsToRegistrationQuestions(cfgAny.stdFields);
    })(),
    // #797 — friendly date+time label via lib/datetime (raw ISO rendered
    // unfriendly AND blanked the Time column: the dates-block renderer
    // force-blanks Time whenever a dateLabel exists, expecting a combined
    // date+time label like the published surface serves).
    dateLabel: (() => {
      if (!cfgAny.start) return undefined;
      const time = friendlyTimeRange(String(cfgAny.start), cfgAny.end ? String(cfgAny.end) : undefined);
      const day = friendlyDate(String(cfgAny.start));
      return time ? `${day} · ${time}` : day;
    })(),
    // Multi-day: one dateLabel/timeLabel pair per calendar day, each day's
    // hours from resolveEventDayHours (the wizard's per-day override when the
    // author set one, else the overall event's start/end time-of-day) — same
    // resolver the Schedule step's day rows read, so the landing preview never
    // drifts from what the step shows.
    days: (() => {
      const resolved = resolveEventDayHours(cfgAny as { start?: string; end?: string; dayHours?: { date: string; start: string; end: string }[] });
      if (resolved.length < 2) return undefined;
      return resolved.map((d) => ({
        dateLabel: friendlyDate(`${d.date}T00:00:00`),
        timeLabel: friendlyTimeRange(`${d.date}T${d.start}:00`, `${d.date}T${d.end}:00`),
      }));
    })(),
    venueLabel: (cfgAny.venueName as string) || address || undefined,
    venueAddress: address || undefined,
  };
}

// ── Main builder component ──────────────────────────────────────────────
export function CreateEventLanding({ standalone = false, pageMeta = null, initialPage = null, embedded = false, scopeTicketTypeIds, onSavePage, onBack }: { standalone?: boolean; pageMeta?: { id?: string; title?: string; slug?: string; status?: string } | null; initialPage?: InitialLandingPage | null; embedded?: boolean; scopeTicketTypeIds?: string[]; onSavePage?: (p: EmbeddedLandingSave) => void; onBack?: () => void }) {
  const t = useT();
  const nav = useNav();
  // #CD-8 / item-3 — public host comes from the tenant public-host composer
  // (getTenantPublicConfig-backed), never a hardcoded jrni.events literal.
  const hostPrefix = publicHost("attend");
  const cfg = useCreationConfig();
  const branding = useBranding();
  const staffStore = useStaff();
  const staffMembers = (staffStore.members as unknown as Record<string, unknown>[]).map((m) => ({ id: String(m.id), name: String(m.name || [m.firstName, m.lastName].filter(Boolean).join(" ") || "Staff"), email: m.email ? String(m.email) : undefined }));
  const staffGroups = (staffStore.groups as unknown as Record<string, unknown>[]).map((g) => ({ id: String(g.id), name: String(g.name || "Group") }));
  // Pure-WYSIWYG canvas (Req 1): the page is FLUID — no fixed device widths
  // and nothing after this UI re-constrains desktop to a max width. vw=null
  // renders full-bleed; dragging the page edges tests any width. // ui-local
  const [vw, setVw] = useState<number | null>(null);
  const canvasScrollRef = useRef<HTMLDivElement>(null);
  const pageColRef = useRef<HTMLDivElement>(null);
  const [liveW, setLiveW] = useState(0);
  const [draggingEdge, setDraggingEdge] = useState(false);
  useEffect(() => { // ui-local — live px readout of the canvas viewport
    const el = pageColRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setLiveW(Math.round(el.getBoundingClientRect().width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const startEdgeDrag = (side: 1 | -1) => (e: React.PointerEvent) => { // ui-local — viewport width testing
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startW = pageColRef.current?.getBoundingClientRect().width || 960;
    const maxW = Math.max(360, (canvasScrollRef.current?.clientWidth || 1600) - 56);
    setDraggingEdge(true);
    const move = (ev: PointerEvent) => {
      const next = Math.round(startW + (ev.clientX - startX) * side * 2);
      setVw(Math.min(maxW, Math.max(320, next)));
    };
    const up = () => { setDraggingEdge(false); window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const [theme, setTheme] = useState<string>(() => {
    // #792 — restore the saved theme on edit-open: standalone gets it from the
    // fetched page (initialPage); the wizard from cfg.landingDesign (hydrated by
    // store.loadEvent → getLandingPage). Falls back to Brand for a new page.
    const t = initialPage?.theme ?? (cfg.landingDesign as { theme?: string } | undefined)?.theme;
    if (t) { const m = LANDING_THEMES.find((x) => x.id === t || x.name === t); if (m) return m.id; }
    return "brand";
  });
  const [font, setFont] = useState(() => initialPage?.font || (cfg.landingDesign as { font?: string } | undefined)?.font || "DM Sans");
  // Adopt the workspace brand body font ONCE on load (only when the user hasn't
  // picked a font and the page didn't come with one).
  const brandFontApplied = useRef(false);
  useEffect(() => {
    if (!brandFontApplied.current && !initialPage?.font && branding.bodyFont && font === "DM Sans") { setFont(branding.bodyFont); brandFontApplied.current = true; }
  }, [branding.bodyFont]); // eslint-disable-line react-hooks/exhaustive-deps
  const [pageTitle, setPageTitle] = useState<string>(() => pageMeta?.title || String(cfg.title || "Landing page"));
  const [slug, setSlug] = useState(() => {
    if (pageMeta?.slug) return pageMeta.slug;
    if (cfg.slug) return String(cfg.slug);
    const title = String(cfg.title || "");
    return title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  });
  const [blocks, setBlocks] = useState<LandingBlock[]>(() => {
    if (initialPage?.blocks && initialPage.blocks.length) return initialPage.blocks;
    if (cfg.landingDesign && Array.isArray((cfg.landingDesign as { blocks?: LandingBlock[] }).blocks)) {
      return (cfg.landingDesign as { blocks: LandingBlock[] }).blocks;
    }
    const title = pageMeta?.title || String(cfg.title || "Event");
    return defaultLandingBlocks(cfg as Record<string, unknown>, title);
  });
  const [selectedUid, setSelectedUid] = useState<string | null>("b1");

  // Hero headline & subhead are always the event title & description (from the
  // Essentials step) — the landing editor displays them but never edits them, so
  // the two stay consistent with the event everywhere they appear. In the
  // standalone editor the visible (renamable) page title is the source instead.
  const eventTitle = standalone ? (pageTitle.trim() || "Event") : String(cfg.title || "Event");
  const eventSubhead = useMemo(() => deriveEventDetails(cfg as Record<string, unknown>).subhead, [cfg]);
  useEffect(() => {
    setBlocks((prev) => {
      const hero = prev.find((b) => b.type === "hero");
      if (!hero) return prev;
      // Standalone: the page's own fetched hero subhead is the truth (the shared
      // CreationStore holds the previously-edited event's description, which
      // would otherwise bleed across pages). Only keep the headline in sync.
      const nextSub = standalone ? hero.cfg.subhead : eventSubhead;
      if (hero.cfg.headline === eventTitle && hero.cfg.subhead === nextSub) return prev;
      return prev.map((b) => b.type === "hero" ? { ...b, cfg: { ...b.cfg, headline: eventTitle, subhead: nextSub } } : b);
    });
  }, [eventTitle, eventSubhead, standalone]);
  // #798 — the landing hero DEFAULTS to the Basics-step hero image (previously
  // applied only at block-add time, so picking a hero in Essentials never pulled
  // through). Applies on change unless the user set a DIFFERENT custom image in
  // the landing editor (tracked via the previous Basics value).
  const basicsHero = String((cfg as Record<string, unknown>).heroImage || "");
  const prevBasicsHero = useRef(basicsHero);
  useEffect(() => {
    // Wizard-only: seed the hero from the event's Basics-step image. In the
    // standalone editor the page's OWN fetched hero is the truth — the shared
    // CreationStore still holds the previously-edited event's heroImage, so
    // running this here bled that image onto the next landing page's hero.
    if (standalone) return;
    const prev = prevBasicsHero.current;
    prevBasicsHero.current = basicsHero;
    if (!basicsHero) return;
    setBlocks((cur) => {
      const hero = cur.find((b) => b.type === "hero");
      if (!hero) return cur;
      const curImg = String(hero.cfg.imageUrl || "");
      if (curImg === basicsHero) return cur;
      const userCustomized = hero.cfg.bgKind === "image" && !!curImg && curImg !== prev;
      if (userCustomized) return cur;
      return cur.map((b) => b.type === "hero" ? { ...b, cfg: { ...b.cfg, bgKind: "image", imageUrl: basicsHero } } : b);
    });
  }, [basicsHero]);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [showCopied, setShowCopied] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  useEffect(() => { // ui-local — Esc closes the guest-preview takeover
    if (!previewOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setPreviewOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [previewOpen]);
  const [past, setPast] = useState<{ blocks: LandingBlock[]; slug: string; theme: string; font: string }[]>([]);
  const [future, setFuture] = useState<typeof past>([]);
  const dragRef = useRef<{ type: string | null; uid: string | null }>({ type: null, uid: null });
  // Blocks removed via the palette toggle are stashed (cfg + uid + original
  // index) so re-checking the same palette tile restores exactly what was there,
  // where it was — instead of a fresh empty block.
  const stashRef = useRef<Record<string, { block: LandingBlock; index: number }[]>>({});

  const currentTheme: LandingTheme = LANDING_THEMES.find(t => t.id === theme) || getDefaultTheme(branding);
  const selected = findBlockDeep(blocks, selectedUid);
  const blockDef = selected ? LANDING_BLOCKS.find(d => d.id === selected.type) : null;

  // WYSIWYG preview hydration (operator item 2, 2026-07-09): the wizard
  // preview's tickets/dates/venue blocks read the wizard's OWN in-progress
  // selections (cfg.tickets etc.) in the same PublicEventDetailLike shape the
  // published surface serves — no more "tiers appear here" with a tier picked.
  // Standalone editing shows the fetched page's canonical event instead.
  const cfgAny = cfg as Record<string, any>;
  const previewEvent = useMemo<PublicEventDetailLike | null>(() => {
    // Embedded per-page editing reflects the wizard's in-progress event tickets
    // (the event isn't saved yet), then scopes them to THIS page's ticket types.
    let ev: PublicEventDetailLike | null = (standalone && !embedded) ? (initialPage?.event ?? null) : previewEventFromCfg(cfgAny);
    if (ev && scopeTicketTypeIds && scopeTicketTypeIds.length) {
      const inScope = new Set(scopeTicketTypeIds.map(String));
      ev = { ...ev, ticketTiers: (ev.ticketTiers || []).filter((t) => inScope.has(String(t.id))) };
    }
    return ev;
    // Depend on the store snapshot itself (fresh object per setField/loadEvent):
    // scalar deps missed later-arriving hydration — loadEvent's stdFields landed
    // after mount on direct entry, so the capture form stayed on the fallback
    // minimal set until the Registration step was visited manually.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standalone, embedded, initialPage, cfg, scopeTicketTypeIds]);

  const snapshot = () => ({ blocks, slug, theme, font });
  const restore  = (s: typeof past[0]) => { setBlocks(s.blocks); setSlug(s.slug); setTheme(s.theme); setFont(s.font); };
  const pushHistory = () => { setPast(p => [...p.slice(-29), snapshot()]); setFuture([]); };
  const undo = () => { if (!past.length) return; const prev = past[past.length - 1]; setFuture(f => [snapshot(), ...f.slice(0, 29)]); setPast(p => p.slice(0, -1)); restore(prev); };
  const redo = () => { if (!future.length) return; const next = future[0]; setPast(p => [...p.slice(-29), snapshot()]); setFuture(f => f.slice(1)); restore(next); };

  const updateCfg = (uid: string, patch: Record<string, unknown>) => {
    pushHistory();
    setBlocks(updateCfgDeep(blocks, uid, patch));
  };
  // Fresh block of `type`, seeded from the event where relevant. Rows get their
  // OWN empty cells (never the shared def.defaults reference).
  const spawnBlock = (type: string): LandingBlock | null => {
    const def = LANDING_BLOCKS.find(d => d.id === type);
    if (!def) return null;
    const d = deriveEventDetails(cfg as Record<string, unknown>);
    const seeded: Record<string, unknown> =
      type === "dates" ? { date: d.datePart || (def.defaults as { date?: string }).date, time: d.timeStr || (def.defaults as { time?: string }).time, loc: d.locStr || (def.defaults as { loc?: string }).loc }
      : type === "map" ? (d.address ? { address: d.address } : {})
      : type === "hero" ? (d.heroImg ? { bgKind: "image", imageUrl: d.heroImg } : {})
      /* The wall clock is kept with its zone (CC 0251), so every visitor counts to the same moment. */
      : type === "countdown" ? ((cfg as { start?: unknown }).start ? { target: String((cfg as { start?: unknown }).start).slice(0, 16), ...(bareZone((cfg as { timezone?: unknown }).timezone) ? { targetZone: bareZone((cfg as { timezone?: unknown }).timezone) } : {}) } : {})
      : {};
    const cfgVal = type === "row"
      ? { ratio: (def.defaults as { ratio?: string }).ratio || "1-1", gap: (def.defaults as { gap?: number }).gap ?? 20, stackMobile: true, cells: [[], []] as LandingBlock[][] }
      : { ...def.defaults, ...seeded };
    return { uid: `b${Date.now().toString(36)}${((Math.random() * 1e6) | 0).toString(36)}`, type, cfg: cfgVal };
  };
  const addBlock = (type: string, atIndex?: number) => {
    if (type === "hero" && blocks.some(b => b.type === "hero")) return;
    const nb = spawnBlock(type);
    if (!nb) return;
    pushHistory();
    const i = atIndex == null ? blocks.length : atIndex;
    setBlocks([...blocks.slice(0, i), nb, ...blocks.slice(i)]);
    setSelectedUid(nb.uid);
  };
  const removeBlock = (uid: string) => {
    pushHistory();
    const next = removeBlockDeep(blocks, uid);
    setBlocks(next);
    if (uid === selectedUid) setSelectedUid(next[0]?.uid || null);
  };
  const toggleBlock = (type: string) => {
    // Columns can appear many times — clicking always adds a fresh row (never
    // toggles every row off and drops their nested content).
    if (type === "row") { addBlock("row"); return; }
    const present = blocks.map((b, i) => ({ b, i })).filter((x) => x.b.type === type);
    if (!present.length) {
      // Re-checking: restore the stashed block(s) at their original index.
      const stash = stashRef.current[type];
      if (stash && stash.length) {
        pushHistory();
        let next = [...blocks];
        stash.slice().sort((a, b) => a.index - b.index).forEach(({ block, index }) => {
          const at = Math.min(index, next.length);
          next = [...next.slice(0, at), block, ...next.slice(at)];
        });
        delete stashRef.current[type];
        setBlocks(next);
        setSelectedUid(stash[0].block.uid);
        return;
      }
      addBlock(type);
      return;
    }
    // Unchecking: stash the removed block(s) (cfg + uid + index), then remove.
    pushHistory();
    stashRef.current[type] = present.map((x) => ({ block: x.b, index: x.i }));
    const next = blocks.filter((b) => b.type !== type);
    setBlocks(next);
    if (present.some((x) => x.b.uid === selectedUid)) setSelectedUid(next[0]?.uid || null);
  };
  const moveBlock = (uid: string, dir: number) => {
    pushHistory();
    setBlocks(moveBlockDeep(blocks, uid, dir));
  };
  const duplicateBlock = (uid: string) => {
    const src = findBlockDeep(blocks, uid);
    if (!src) return;
    const clone = cloneBlockDeep(src);
    pushHistory();
    setBlocks(insertAfterDeep(blocks, uid, clone));
    setSelectedUid(clone.uid);
  };
  // Drop ON a block: insert the dragged item (new-from-palette or an existing
  // block moved from anywhere) immediately before the target, in the target's
  // OWN container — so this one handler reorders at top level, reorders within a
  // column, and moves blocks between top level and columns.
  const onDropOnBlock = (targetUid: string) => {
    const src = dragRef.current;
    dragRef.current = { type: null, uid: null };
    setDragOver(null);
    const intoCell = !!parentRowOf(blocks, targetUid);
    if (src.type) {
      if (intoCell && CELL_FORBIDDEN.has(src.type)) return;
      const nb = spawnBlock(src.type);
      if (!nb) return;
      pushHistory();
      setBlocks(prev => insertBeforeDeep(prev, targetUid, nb));
      setSelectedUid(nb.uid);
    } else if (src.uid && src.uid !== targetUid) {
      const found = findBlockDeep(blocks, src.uid);
      if (!found) return;
      if (intoCell && CELL_FORBIDDEN.has(found.type)) return;
      // Never drop a row onto one of its own descendants.
      if (found.type === "row" && findBlockDeep(([] as LandingBlock[]).concat(...getRowCells(found.cfg)), targetUid)) return;
      pushHistory();
      setBlocks(prev => insertBeforeDeep(removeBlockDeep(prev, src.uid!), targetUid, found));
      setSelectedUid(found.uid);
    }
  };
  // Drop onto a column's empty space — append to that cell.
  const onDropToCell = (rowUid: string, cellIdx: number) => {
    const src = dragRef.current;
    dragRef.current = { type: null, uid: null };
    setDragOver(null);
    if (src.type) {
      if (CELL_FORBIDDEN.has(src.type)) return;
      const nb = spawnBlock(src.type);
      if (!nb) return;
      pushHistory();
      setBlocks(prev => appendToCellDeep(prev, rowUid, cellIdx, nb));
      setSelectedUid(nb.uid);
    } else if (src.uid && src.uid !== rowUid) {
      const found = findBlockDeep(blocks, src.uid);
      if (!found || CELL_FORBIDDEN.has(found.type)) return;
      pushHistory();
      setBlocks(prev => appendToCellDeep(removeBlockDeep(prev, src.uid!), rowUid, cellIdx, found));
      setSelectedUid(found.uid);
    }
  };
  const onCopyUrl = () => {
    const url = withUtm(`https://${hostPrefix}/${slug}`, { source: SHARE_SOURCES.landingBuilder, medium: SHARE_MEDIUM.link, campaign: slug });
    try { navigator.clipboard?.writeText(url); } catch {}
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 1400);
  };
  const commitLanding = () => {
    // Embedded per-page editing writes back to the manager's page list instead
    // of the singular wizard landingDesign.
    if (embedded && onSavePage) {
      onSavePage({
        id: pageMeta?.id || pageId,
        name: pageTitle.trim() || "Untitled page",
        slug,
        design: { blocks, theme, font },
        status: (pageMeta?.status === "published" ? "published" : "draft"),
      });
      return;
    }
    CreationStore.setField("slug", slug);
    // Landing headline/subhead are the event title & description — keep them in
    // lockstep so the Review & publish summary matches the event.
    CreationStore.setField("landingHeadline", eventTitle);
    CreationStore.setField("landingSubhead", eventSubhead);
    CreationStore.setField("landingDesign", { blocks, theme, font });
  };
  const commitRef = useRef(commitLanding); commitRef.current = commitLanding;
  useEffect(() => () => { try { commitRef.current(); } catch {} }, []);

  // Interim client persistence — a page saved/published in the standalone editor
  // shows on the Landing Pages list immediately (keyed by the event/page id).
  const [pageId] = useState(() => pageMeta?.id || `lp_${Date.now().toString(36)}`);
  const persistPage = (status: "draft" | "published") => {
    const title = pageTitle.trim() || "Untitled page";
    upsertLandingPage({ id: pageId, title, slug, ev: title, hue: currentTheme.hero?.[1] || "#75ADFA", status, visits: 0, conv: 0, convPct: 0, edited: "Just now", theme: currentTheme.name });
  };

  // Server persistence — the WYSIWYG contract: blocks land in
  // landing_pages.blocks_json via the typed client, so the public attend page
  // (getPublishedLandingSurface) returns exactly this block array. create →
  // update → publish; awaited, never a fire-and-forget toast.
  const [savedId, setSavedId] = useState<string | null>(() => initialPage?.id ?? null);
  const [saving, setSaving] = useState<"draft" | "publish" | null>(null);
  const eventId = String((cfg as { id?: unknown; eventId?: unknown }).id || (cfg as { eventId?: unknown }).eventId || pageMeta?.id || pageId);
  async function saveToBackend(publish: boolean) {
    const opts = studioRequestOptions("landing-pages");
    const input = {
      eventId,
      slug,
      title: pageTitle.trim() || "Untitled page",
      status: (publish ? "published" : "draft") as "published" | "draft",
      blocks,
      theme: currentTheme.name,
      font,
      globalCss: "",
    };
    const saved = savedId
      ? await updateLandingPage(savedId, input as Parameters<typeof updateLandingPage>[1], opts)
      : await createLandingPage(input as Parameters<typeof createLandingPage>[0], opts);
    if (!savedId && saved?.id) setSavedId(saved.id);
    if (publish) await publishLandingPage(saved.id, opts, { status: "published" });
    return saved;
  }
  const onSaveDraft = async () => {
    if (saving) return;
    setSaving("draft");
    try {
      commitLanding();
      await saveToBackend(false);
      persistPage("draft");
      toast({ tone: "success", title: t("landing.builder.draftSaved", "Draft saved") });
    } catch (e) {
      console.warn("landing save-draft failed:", e);
      toast({ tone: "danger", title: t("landing.builder.couldnTSaveTheDraft", "Couldn't save the draft"), subtitle: t("landing.builder.nothingWasSavedRetryInAMoment", "Nothing was saved. Retry in a moment.") });
    } finally { setSaving(null); }
  };
  const onPublish = async () => {
    if (saving) return;
    setSaving("publish");
    try {
      commitLanding();
      await saveToBackend(true);
      persistPage("published");
      toast({ tone: "success", title: t("common.status.published", "Published"), subtitle: t("landing.builder.hostprefixSlug.icu", "{value0}/{value1}", { value0: hostPrefix, value1: slug }) });
      nav.go("landing.pages");
    } catch (e) {
      console.warn("landing publish failed:", e);
      toast({ tone: "danger", title: t("landing.builder.couldnTPublishThePage", "Couldn't publish the page"), subtitle: t("landing.builder.itStaysADraftRetryInAMoment", "It stays a draft. Retry in a moment.") });
    } finally { setSaving(null); }
  };

  // Validation gate for Continue button
  const missing: string[] = [];
  if (!slug?.trim()) missing.push("URL slug");
  if (!blocks.length) missing.push("at least one block");
  // The hero headline is the event title (required in the Essentials step), so
  // the landing step only flags it if that upstream field is still empty.
  if (!standalone && !String(cfg.title || "").trim()) missing.push("event title (Essentials step)");
  const nextDisabled = missing.length > 0;

  const body = (
    <>
      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        {/* Left rail — block types + theme + slug */}
        <div style={{ width: 260, background: T.white, borderRight: `1px solid ${T.border}`, padding: 16, display: "flex", flexDirection: "column", gap: 18, overflow: "auto" }}>
          <div>
            <div style={tx.label}>{t("landing.builder.dragBlocksOntoPage", "Drag blocks onto page")}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 8 }}>
              {(() => {
                // Types present ANYWHERE in the tree (top level + nested column
                // cells) so a block dropped into a column also shows as in-use.
                const usedTypes = new Set<string>();
                const collect = (list: LandingBlock[]) => list.forEach((bl) => { usedTypes.add(bl.type); if (bl.type === "row") getRowCells(bl.cfg).forEach(collect); });
                collect(blocks);
                return landingBlocksFor("landing").map((b) => {
                const used = usedTypes.has(b.id);
                return (
                  <div key={b.id} draggable onClick={() => b.id === "row" ? addBlock("row") : toggleBlock(b.id)}
                    onDragStart={() => { dragRef.current = { type: b.id, uid: null }; }}
                    onDragEnd={() => { dragRef.current = { type: null, uid: null }; setDragOver(null); }}
                    style={{ padding: "12px 8px", border: `1px solid ${used ? T.p1 : T.border}`, borderRadius: 6, background: used ? T.p4 : T.white, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: used ? T.p1 : T.n1, fontWeight: used ? 600 : 400, position: "relative" }}>
                    {b.l}
                    {used ? <span style={{ position: "absolute", top: 4, right: 4, width: 14, height: 14, borderRadius: 999, background: T.p1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.check size={9}/></span> : null}
                  </div>
                );
              });
              })()}
            </div>
          </div>
          <div>
            <div style={tx.label}>{t("landing.builder.theme", "Theme")}</div>
            <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
              {/* ui-raw: bespoke circular gradient theme-swatch picker (not a
                  labeled button/control — the swatch IS the affordance) */}
              {[getDefaultTheme(branding), ...LANDING_THEMES].map((tm) => (
                /* ui-raw: theme swatch (gradient preview) — ColorPicker's pattern (group 10) */
                <button key={tm.id} onClick={() => setTheme(tm.id)} title={tm.name}
                  style={{ width: 28, height: 28, borderRadius: 999, background: `linear-gradient(135deg, ${tm.hero[0]}, ${tm.hero[1]})`, border: theme === tm.id ? `2px solid ${T.n1}` : "2px solid transparent", boxShadow: theme === tm.id ? `0 0 0 2px ${T.p1}` : `0 0 0 1px ${T.border}`, cursor: "pointer" }}/>
              ))}
            </div>
            <div style={{ fontSize: 11, color: T.soft, marginTop: 6 }}>{currentTheme.name}</div>
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 }}>{t("landing.builder.font", "Font")}</label>
              <Dropdown value={font} onChange={(v) => setFont(v)}
                options={["DM Sans","Inter","Source Sans Pro","Avenir Next","Georgia","Helvetica Neue"].map(f => ({ value: f, label: f }))}/>
            </div>
          </div>
          <div>
            <div style={tx.label}>{t("landing.builder.urlSlug", "URL slug")}</div>
            <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
              leading={<span style={{ color: T.soft, whiteSpace: "nowrap" }}>{hostPrefix}/</span>}
              style={{ marginTop: 8 }}/>
            </div>
          {/* Undo/redo */}
          <div style={{ display: "flex", gap: 6 }}>
            <Btn variant="ghost" size="sm" disabled={!past.length} onClick={undo} icon={<Icon.arrow dir="left" size={11}/>}>{t("landing.builder.undo", "Undo")}</Btn>
            <Btn variant="ghost" size="sm" disabled={!future.length} onClick={redo} iconRight={<Icon.arrow size={11}/>}>{t("landing.builder.redo", "Redo")}</Btn>
          </div>
        </div>

        {/* Centre — device preview */}
        <div style={{ flex: 1, background: T.canvas, overflow: "hidden", display: "flex", flexDirection: "column", position: "relative", minWidth: 0 }}>
          <div ref={canvasScrollRef} style={{ flex: 1, padding: "20px 28px", overflow: "auto" }}>
            <div ref={pageColRef} style={{ position: "relative", width: vw ?? "100%", maxWidth: "100%", margin: "0 auto", transition: draggingEdge ? "none" : "width 200ms ease" }} onDragOver={(e) => e.preventDefault()}>
              <LandingPreview blocks={blocks} theme={currentTheme} font={font} slug={slug} event={previewEvent}
                selectedUid={selectedUid} onSelect={setSelectedUid}
                onRemove={removeBlock} onMove={moveBlock}
                dragOver={dragOver} onDragOverBlock={(uid) => setDragOver(uid)}
                onDrop={onDropOnBlock} onCellDrop={onDropToCell} onDuplicate={duplicateBlock} onDragStartBlock={(uid) => { dragRef.current = { type: null, uid }; }}/>
              <div onDragOver={(e) => { e.preventDefault(); setDragOver("end"); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={() => {
                  const src = dragRef.current;
                  dragRef.current = { type: null, uid: null };
                  setDragOver(null);
                  if (src.type) { addBlock(src.type); return; }
                  if (src.uid) { const f = findBlockDeep(blocks, src.uid); if (f) { pushHistory(); setBlocks(prev => [...removeBlockDeep(prev, src.uid!), f]); setSelectedUid(f.uid); } }
                }}
                style={{ marginTop: 12, padding: "20px 16px", borderRadius: 8, border: `2px dashed ${dragOver === "end" ? T.p1 : T.n4}`, background: dragOver === "end" ? T.p4 : "transparent", textAlign: "center", color: T.soft, fontSize: 13 }}>
                <Icon.plus size={14}/> {t("landing.builder.dropABlockHere", "Drop a block here")}</div>
              {([-1, 1] as const).map((side) => (
                <div key={side} onPointerDown={startEdgeDrag(side)} title={t("landing.builder.dragToTestAnyWidth", "Drag to test any width")}
                  style={{ position: "absolute", top: 0, bottom: 0, [side === -1 ? "left" : "right"]: -22, width: 20, display: "flex", alignItems: "center", justifyContent: "center", cursor: "ew-resize", touchAction: "none" } as React.CSSProperties}>
                  <span style={{ width: 5, height: 48, borderRadius: 999, background: draggingEdge ? T.p1 : T.n4, transition: "background 120ms" }}/>
                </div>
              ))}
              {draggingEdge ? (
                <div style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", zIndex: 5, background: T.n1, color: T.white, fontSize: 11, fontFamily: T.fontMono, borderRadius: 4, padding: "3px 8px", pointerEvents: "none" }}>{liveW}{t("landing.builder.px", "px")}</div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Right — inspector */}
        <div style={{ width: 300, background: T.white, borderLeft: `1px solid ${T.border}`, padding: 16, display: "flex", flexDirection: "column", gap: 14, overflow: "auto" }}>
          {selected && blockDef ? (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={tx.label}>{t("landing.builder.selected", "Selected ·")}{blockDef.l}</div>
                <div style={{ display: "flex", gap: 4 }}>
                  <IconBtn size={28} label={t("landing.builder.moveUp", "Move up")}   onClick={() => moveBlock(selected.uid, -1)}><Icon.chev dir="up" size={12}/></IconBtn>
                  <IconBtn size={28} label={t("landing.builder.moveDown", "Move down")} onClick={() => moveBlock(selected.uid, +1)}><Icon.chev size={12}/></IconBtn>
                  <IconBtn size={28} label={t("landing.builder.duplicate", "Duplicate")} onClick={() => duplicateBlock(selected.uid)}><Icon.copy size={12}/></IconBtn>
                  <IconBtn size={28} label={t("common.actions.delete", "Delete")}    onClick={() => removeBlock(selected.uid)}><Icon.trash size={12}/></IconBtn>
                </div>
              </div>
              <BlockInspector type={selected.type} cfg={selected.cfg} onChange={(patch) => updateCfg(selected.uid, patch)} allBlocks={blocks} staffMembers={staffMembers} staffGroups={staffGroups}/>
            </>
          ) : (
            <div style={{ padding: 16, textAlign: "center", color: T.soft, fontSize: 13 }}>
              <div style={{ marginTop: 8 }}>{t("landing.builder.selectABlockToEditItOrDragFromTheLeft", "Select a block to edit it, or drag from the left.")}</div>
            </div>
          )}
          <hr style={{ border: "none", borderTop: `1px solid ${T.border}`, margin: "6px 0" }}/>
          <div>
            <div style={tx.label}>{t("landing.builder.pageOutline", "Page outline")}</div>
            <ol style={{ listStyle: "none", margin: "8px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
              {(() => {
                const rows: { b: LandingBlock; depth: number }[] = [];
                const walk = (list: LandingBlock[], depth: number) => list.forEach((b) => {
                  rows.push({ b, depth });
                  if (b.type === "row") getRowCells(b.cfg).forEach((cell) => walk(cell, depth + 1));
                });
                walk(blocks, 0);
                return rows.map(({ b, depth }, i) => {
                  const def = LANDING_BLOCKS.find(d => d.id === b.type);
                  return (
                    <li key={b.uid} onClick={() => setSelectedUid(b.uid)}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", paddingLeft: 8 + depth * 14, borderRadius: 4, background: selectedUid === b.uid ? T.p4 : "transparent", cursor: "pointer", border: selectedUid === b.uid ? `1px solid ${T.p2}` : `1px solid transparent` }}>
                      {depth > 0 ? <span style={{ color: T.n4, fontSize: 11, fontFamily: T.fontMono }}>&#8627;</span> : null}
                      <span style={{ fontSize: 13, color: T.n1, flex: 1, fontWeight: b.type === "row" ? 600 : 400 }}>{def?.l || b.type}</span>
                      <span style={{ fontSize: 10, color: T.soft, fontFamily: T.fontMono }}>{i + 1}</span>
                    </li>
                  );
                });
              })()}
            </ol>
          </div>
        </div>
      </div>

      {/* Guest preview — full-screen, FULL-BLEED takeover: exactly the attend
          page render (same LandingPreview, no width constraint, no chrome). */}
      {previewOpen ? (
        <div style={{ position: "fixed", inset: 0, zIndex: 200, background: T.white, display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", background: T.n1, color: T.white, flexShrink: 0 }}>
            <Icon.eye size={14}/>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{t("landing.builder.guestPreview", "Guest preview")}</span>
            <span style={{ fontSize: 12, fontFamily: T.fontMono, opacity: 0.75 }}>{hostPrefix}/{slug || "your-event"}</span>
            <span style={{ marginLeft: "auto", fontSize: 11.5, opacity: 0.75 }}>{t("landing.builder.fullBleedExactlyWhatGuestsWillSeeEscToClose", "Full-bleed — exactly what guests will see · Esc to close")}</span>
            <Btn variant="secondary" size="sm" icon={<Icon.x size={12}/>} onClick={() => setPreviewOpen(false)}>{t("common.actions.close", "Close")}</Btn>
          </div>
          <div style={{ flex: 1, overflow: "auto", background: T.white }}>
            <LandingPreview blocks={blocks} theme={currentTheme} font={font} slug={slug} event={previewEvent} chrome={false}/>
          </div>
        </div>
      ) : null}
    </>
  );

  // ── Embedded mode (per-page editing inside the wizard Landing step) ───
  if (embedded) {
    const embeddedFooter = (
      <>
        <Btn variant="secondary" icon={<Icon.arrow dir="left" size={14}/>} onClick={() => { commitLanding(); onBack?.(); }}>{t("landing.builder.backToPages", "Back to pages")}</Btn>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* ui-raw: chromeless inline copy-url trigger (no border/padding/height —
              Btn's link/ghost variants impose fixed sizing that would change this
              footer's subtle inline look) with its own "Copied!" inline feedback,
              distinct from CopyableUrl's boxed layout + toast */}
          <button onClick={onCopyUrl} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "transparent", border: "none", cursor: "pointer", fontSize: 12, color: T.soft, fontFamily: T.font }}>
            {hostPrefix}/{slug} <Icon.copy size={12} style={{ color: T.p1, marginLeft: 4 }}/>
            {showCopied ? <span style={{ marginLeft: 6, color: T.green1, fontWeight: 600 }}>{t("landing.builder.copied", "Copied!")}</span> : null}
          </button>
          <Btn variant="secondary" icon={<Icon.eye size={14}/>} onClick={() => { commitLanding(); setPreviewOpen(true); }}>{t("landing.builder.preview", "Preview")}</Btn>
          <Btn icon={<Icon.check size={14}/>} disabled={nextDisabled} onClick={() => { commitLanding(); onBack?.(); }}>{t("landing.builder.savePage", "Save page")}</Btn>
        </div>
      </>
    );
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", background: T.canvas }}>{body}</div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 20px", borderTop: `1px solid ${T.border}`, background: T.white ?? "#FFFFFF" }}>
          {embeddedFooter}
        </div>
      </div>
    );
  }

  // ── Standalone mode (landing.editor) ──────────────────────────────────
  if (standalone) {
    return (
      <Screen rail="events" tabs={eventsSectionTabs(nav, "landing-pages", useEventSectionCounts())} activeTab="landing-pages">
        <PageHeader
          /* ui-raw: inline heading-scale title editor — HTML `size` attr sizes
             the field to its own character count so it reads as page-title
             text, not a form field; the jrni-ui Input imposes a fixed 40px
             control chrome that would break the header's typography */
          title={<input value={pageTitle} placeholder={t("landing.builder.untitledPage", "Untitled page")} aria-label={t("landing.builder.pageName", "Page name")}
            size={Math.max(24, pageTitle.length + 2)}
            onChange={(e) => { setPageTitle(e.target.value); CreationStore.setField("title", e.target.value); }}
            style={{ font: "inherit", color: "inherit", border: "none", borderBottom: `1px dashed ${T.n4}`, background: "transparent", padding: "0 2px 2px", maxWidth: "100%", fontFamily: T.font }} />}
          /* ui-raw: jrni-ui Breadcrumb renders a lone item as the terminal
             (non-interactive, aria-current) crumb — it would silently drop
             this back-link's onClick, so a single hand-rolled clickable
             crumb stays here to preserve navigation */
          breadcrumb={<span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("landing.pages")}><Icon.arrow dir="left" size={11}/> {t("landing.builder.landingPages", "Landing pages")}</span>}
          actions={<>
            <Btn variant="ghost" disabled={!!saving} onClick={onSaveDraft}>{saving === "draft" ? t("landing.landingBuilder.saving", "Saving…") : t("landing.landingBuilder.saveDraft", "Save draft")}</Btn>
            <Btn variant="secondary" icon={<Icon.eye size={14}/>} onClick={() => { commitLanding(); setPreviewOpen(true); }}>{t("landing.builder.preview", "Preview")}</Btn>
            <Btn icon={<Icon.send size={14}/>} disabled={!!saving} onClick={onPublish}>{saving === "publish" ? t("landing.landingBuilder.publishing", "Publishing…") : t("common.actions.publish", "Publish")}</Btn>
          </>}/>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, background: T.canvas, borderTop: `1px solid ${T.border}`, marginTop: 16 }}>
          {body}
        </div>
      </Screen>
    );
  }

  /* The third mode — the create.landing WIZARD STEP — was removed with the
     wizard (2026-08-24, operator). A landing page is the journey's entry page
     now; this builder keeps its two live modes, `embedded` (the hubs surface,
     above) and `standalone` (landing.editor). Anything reaching here has asked
     for neither, so it renders the standalone frame rather than a wizard step
     that no longer exists. */
  return (
    <Screen rail="events" tabs={eventsSectionTabs(nav, "landing-pages", useEventSectionCounts())} activeTab="landing-pages">
      <PageHeader title={pageTitle || t("landing.builder.untitledPage", "Untitled page")}
        actions={<Btn variant="secondary" icon={<Icon.eye size={14}/>} onClick={() => { commitLanding(); setPreviewOpen(true); }}>{t("landing.builder.preview", "Preview")}</Btn>}/>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, background: T.canvas, borderTop: `1px solid ${T.border}`, marginTop: 16 }}>
        {body}
      </div>
    </Screen>
  );
}

// ── Route target for landing.editor ────────────────────────────────────
export default function LandingPageEditor() {
  const t = useT();
  const nav = useNav();
  const id = (nav.params?.id || nav.params?.eventId) as string | undefined;
  // Fetch the SAVED page on mount (operator item, 2026-07-09): the editor
  // previously synthesized a placeholder draft regardless of D1 state — only
  // the eyeball route fetched. A failed fetch (new page / 404) falls through
  // to a fresh builder honestly; it never fakes a saved "draft" status.
  const [state, setState] = useState<{ status: "loading" | "ready"; meta: { id?: string; title?: string; slug?: string; status?: string } | null; page: InitialLandingPage | null }>(
    () => (id ? { status: "loading", meta: null, page: null } : { status: "ready", meta: null, page: null }),
  );
  useEffect(() => {
    if (!id) return;
    let live = true;
    // The event's own registration config must reach the capture form on EVERY
    // entry path — saved page, fresh page (getLandingPage 404s), or a surface
    // whose `event` projection omits registrationQuestions (REQ-030). Compose
    // from the canonical getStudioEvent op; a miss leaves the honest fallback.
    const withRegQs = async (event: PublicEventDetailLike | null, evId: string): Promise<PublicEventDetailLike | null> => {
      const has = Array.isArray((event as Record<string, unknown> | null)?.registrationQuestions) && ((event as Record<string, any>).registrationQuestions as unknown[]).length > 0;
      if (has) return event;
      try {
        const dto = await getStudioEvent(evId, studioRequestOptions("events")) as Record<string, any>;
        const regQs = Array.isArray(dto?.registrationQuestions) && dto.registrationQuestions.length
          ? dto.registrationQuestions
          // Event exists but has no saved array — publishing it would emit the
          // #816 default set, so the WYSIWYG form shows exactly that.
          : stdFieldsToRegistrationQuestions(undefined);
        return { ...(event || { title: String(dto.title || "") }), registrationQuestions: regQs };
      } catch (e) { console.warn("getStudioEvent (standalone landing hydration) failed — keeping surface event:", e); }
      return event;
    };
    getLandingPage(id, studioRequestOptions("landing-pages"))
      .then(async (r) => {
        const p = r as Record<string, any>;
        const evId = String(p.eventId || (p.event as Record<string, any>)?.id || id).replace(/^lp-/, "");
        const event = await withRegQs((p.event ?? null) as PublicEventDetailLike | null, evId);
        if (!live) return;
        setState({
          status: "ready",
          meta: { id: String(p.id ?? id), title: String(p.title ?? p.ev ?? "Landing page"), slug: p.slug ? String(p.slug) : undefined, status: p.status ? String(p.status) : undefined },
          page: {
            id: String(p.id ?? id),
            blocks: Array.isArray(p.blocks) && p.blocks.length ? (p.blocks as LandingBlock[]) : undefined,
            theme: (p.themeConfig?.id ?? p.themeConfig?.name ?? p.theme) as string | undefined,
            font: p.font ? String(p.font) : undefined,
            event,
          },
        });
      })
      .catch(async (e) => {
        console.warn("getLandingPage failed — starting a fresh page:", e);
        // Fresh page for an existing event: no saved surface, but the event's
        // registration config still drives the default sign-up block.
        const event = await withRegQs(null, String(id).replace(/^lp-/, ""));
        if (live) setState({ status: "ready", meta: { id }, page: event ? { id, event } : null });
      });
    return () => { live = false; };
  }, [id]);
  if (state.status === "loading") {
    return <div style={{ minHeight: "100%", background: T.canvas, padding: 80, textAlign: "center", color: T.soft, fontSize: 15 }}>{t("landing.builder.loadingPage", "Loading page…")}</div>;
  }
  return <CreateEventLanding key={state.page?.id || id || "new"} standalone pageMeta={state.meta} initialPage={state.page}/>;
}
